import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";
import { useCurrentLocation } from "./useCurrentLocation";
import { ILocationRate } from "../utils/locationRates";

export interface IMarketPricing {
  _id?: string;
  name: string;
  measurement: string;
  image: string;
  type: string;
  rate: number;
  co2_rate?: number;
  linkedArticle?: string | null;
  droppie_revenue_kg?: number;
  droppie_revenue_item?: number;
  droppie_revenue_month?: number;
  kgConversion: number;
  maxAllowed?: number;
  cbs: number;
  order?: number;
  dropType?: string;
  keywords?: string[];
  isActive?: boolean;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  /** CASH (default) credits the user balance; PARTNER_VOUCHER issues a
   *  partner discount voucher instead (stream contributes zero to price). */
  rewardType?: "CASH" | "PARTNER_VOUCHER";
  voucherReward?: {
    partnerId?: string | Record<string, any> | null;
    codeDisplayFormat?: "TEXT" | "QR";
  };
  /** Locations where the consumer payout differs from `rate`. Use
   *  effectiveRate() to get the rate at a location. */
  locationRates?: ILocationRate[];
}

/** Assembles the reward-policy part of a material payload from the flat
 *  form fields used by the Add/Edit MarketPricing screens. */
export const buildRewardPayload = (data: {
  rewardType?: string;
  voucherRewardPartnerId?: string;
}) => ({
  rewardType: (data?.rewardType || "CASH") as "CASH" | "PARTNER_VOUCHER",
  ...(data?.rewardType === "PARTNER_VOUCHER"
    ? {
        voucherReward: {
          partnerId: data?.voucherRewardPartnerId,
          codeDisplayFormat: "QR" as const,
        },
      }
    : {}),
});

export interface IMarketPriceStats {
  _id?: string;
  totalDrops?: number;
  /** The amount in those drops, in the material's unit (items, or kg). */
  totalItems?: number;
  peopleCount?: number;
}

export interface IWeeklyMarketStat {
  [key: string]: { totalWeight: number; totalPrice: number };
}

/** One audit row returned by GET /drops/market/:id/history — captures who
 *  edited a material and which numeric fields moved between before/after. */
export interface IMarketPricingHistoryEntry {
  _id: string;
  material: string;
  materialName: string;
  materialType: string;
  changedBy:
    | {
        _id: string;
        first_name?: string;
        last_name?: string;
        email?: string;
      }
    | string
    | null;
  changedByName?: string | null;
  /** Why the change happened when it was not a direct edit of the material,
   *  e.g. "Location Store Utrecht deleted". */
  reason?: string | null;
  changedFields: string[];
  before: Record<string, any>;
  after: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface IMarketPricingHistoryResponse {
  items: IMarketPricingHistoryEntry[];
  total: number;
  page: number;
  limit: number;
}

interface IStore {
  /** State */
  weeklyMarketStats: IWeeklyMarketStat | null;
  oneMarketPrice: IMarketPricing | null;
  marketPrices: IMarketPricing[];
  marketPriceStats: IMarketPriceStats | null;
  allMarketPriceStats: IMarketPriceStats[] | [];
  addedMarketPricing: boolean;
  updatedMarketPricing: boolean;
  /** Per-material price-change audit log shown on EditMarketPricing. */
  marketPricingHistory: IMarketPricingHistoryResponse | null;
  isLoadingMarketPricingHistory: boolean;
  /** Actions */
  getWeeklyMarketStats: (id: string, requestedDay?: Date) => void;
  getAllMarketPriceStats: () => void;
  getMarketPricingStats: (id: string) => void;
  getAllMarketPrices: () => void;
  getOneMarketPrice: (id: string) => void;
  addMarketPricing: (marketPricing: IMarketPricing) => void;
  updateMarketPricing: (
    id: string,
    marketPricing: Partial<IMarketPricing>
  ) => void;
  updateMarketPricingLinkedArticle: (id: string, linkedArticle: string) => void;
  deleteMarketPricing: (id: string) => void;
  getMarketPricingHistory: (
    id: string,
    options?: { page?: number; limit?: number },
  ) => Promise<void>;
  clearMarketPricingHistory: () => void;
  clearAddedMarketPricing: () => void;
  clearUpdatedMarketPricing: () => void;
}

/**
 * Mirrors the backend's own `sort({ order: 1 })` on GET /drops/market, so a
 * material lands in its new position straight after a save instead of only
 * after a full page reload. Without this the store keeps the array order it
 * was first served in: an update replaces the item in place and a create
 * appends to the end.
 *
 * `order` is optional on older documents; the model defaults it to 0, so an
 * absent value sorts as 0 here too rather than to the bottom.
 */
const byListOrder = (a: IMarketPricing, b: IMarketPricing) =>
  Number(a?.order ?? 0) - Number(b?.order ?? 0);

export const useMarketPricing = create<IStore>()((set, get) => ({
  /** State */
  weeklyMarketStats: null,
  allMarketPriceStats: [],
  marketPriceStats: null,
  oneMarketPrice: null,
  marketPrices: [],
  addedMarketPricing: false,
  updatedMarketPricing: false,
  marketPricingHistory: null,
  isLoadingMarketPricingHistory: false,
  /** Actions */
  getWeeklyMarketStats: async (id, requestedDay = new Date()) => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `/drops/market/stats/weekly/${id}?requestedDay=${requestedDay}&location=${currentLocation}`
      );
      if (response?.data) {
        set({ weeklyMarketStats: response?.data });
      }
    } catch (error) {
      LOG.error("Error on fetching drops dashboard stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getAllMarketPriceStats: async () => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `drops/market/stats?location=${currentLocation}`
      );
      if (response?.data) {
        set({ allMarketPriceStats: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching all Market pricing stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getMarketPricingStats: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `drops/market/stats/${id}?location=${currentLocation}`
      );
      if (response?.data) {
        set({ marketPriceStats: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Market pricing stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getAllMarketPrices: async () => {
    const currentMarketPrices = get().marketPrices;
    if (currentMarketPrices?.length) return;
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get("drops/market");
      if (response?.data) {
        set({ marketPrices: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching Market pricing", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getOneMarketPrice: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`drops/market/${id}`);
      if (response?.data) {
        set({ oneMarketPrice: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching one Market pricing", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addMarketPricing: async (marketPricing: IMarketPricing) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("drops/market", marketPricing);
      if (response?.data) {
        set((state) => ({
          marketPrices: [...state.marketPrices, response.data].sort(
            byListOrder
          ),
          addedMarketPricing: response?.data?._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on adding new Market pricing", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateMarketPricing: async (
    id: string,
    marketPricing: Partial<IMarketPricing>
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`drops/market/${id}`, marketPricing);
      if (response?.data) {
        const updatedMarketPrice = response.data;
        set((state) => ({
          marketPrices: state?.marketPrices
            .map((mp) => {
              if (mp._id === updatedMarketPrice._id) {
                return updatedMarketPrice;
              }
              return mp;
            })
            .sort(byListOrder),
          updatedMarketPricing: updatedMarketPrice._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on updating Market pricing", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateMarketPricingLinkedArticle: async (
    id: string,
    linkedArticle: string
  ) => {
    try {
      await axios.put(`drops/market/article/${id}`, {
        linkedArticle,
      });
    } catch (error) {
      LOG.error("Error on updating Market pricing", { error });
    }
  },
  deleteMarketPricing: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`drops/market/${id}`);
      if (response?.data) {
        set((state) => ({
          marketPrices: state.marketPrices?.filter((item) => item._id !== id),
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting Market pricing", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getMarketPricingHistory: async (id, options) => {
    set({ isLoadingMarketPricingHistory: true });
    try {
      const params = new URLSearchParams();
      if (options?.page) params.append("page", String(options.page));
      if (options?.limit) params.append("limit", String(options.limit));
      const qs = params.toString();
      const response = await axios.get(
        `drops/market/${id}/history${qs ? `?${qs}` : ""}`,
      );
      if (response?.data) {
        set({ marketPricingHistory: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching market pricing history", { error });
    } finally {
      set({ isLoadingMarketPricingHistory: false });
    }
  },
  clearMarketPricingHistory: () => set({ marketPricingHistory: null }),
  clearAddedMarketPricing: () => set({ addedMarketPricing: false }),
  clearUpdatedMarketPricing: () => set({ updatedMarketPricing: false }),
}));
